import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import PageHeader from "@/components/PageHeader";
import { fetchAutopilotTriggers, type AutopilotTrigger } from "@/api/autopilot";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { usePageBreadcrumbs } from "@/providers/BreadcrumbProvider";

const TRIGGER_BREADCRUMBS = [
  { label: "Automation", href: "/automation/triggers" },
  { label: "Trigger History" },
];

export default function WorkflowTriggerHistoryPage() {
  const [triggers, setTriggers] = useState<AutopilotTrigger[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  usePageBreadcrumbs(TRIGGER_BREADCRUMBS);

  const loadTriggers = () => {
    setLoading(true);
    setError(null);
    fetchAutopilotTriggers()
      .then((payload) => setTriggers(payload.items ?? []))
      .catch((err) => setError(err instanceof Error ? err.message : "Unable to load trigger history."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTriggers();
  }, []);

  return (
    <div className="aa-page-shell space-y-6">
      <PageHeader
        title="Workflow trigger history"
        description="Mapping between CRM events and Autopilot workflow runs."
        actions={
          <Button variant="outline" onClick={loadTriggers} disabled={loading}>
            Refresh
          </Button>
        }
      />
      {error && <p className="text-xs text-rose-400">{error}</p>}
      <Card className="aa-glass-card">
        <CardHeader>
          <CardTitle className="text-base text-foreground">Recent triggers</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Event type</TableHead>
                <TableHead>Workflow</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Nodes executed</TableHead>
                <TableHead>Graph run</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {triggers.map((trigger) => (
                <TableRow key={trigger.id}>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(trigger.timestamp).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-sm text-foreground">{trigger.event_type}</TableCell>
                  <TableCell className="text-sm text-foreground">{trigger.workflow_id}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        trigger.status === "completed"
                          ? "border-emerald-400 text-emerald-200"
                          : trigger.status === "failed"
                            ? "border-rose-400 text-rose-200"
                            : "border-primary/60 text-foreground"
                      }
                    >
                      {trigger.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{trigger.nodes_executed ?? "—"}</TableCell>
                  <TableCell>
                    {trigger.graph_run_id ? (
                      <Link
                        to={`/graph/workflows/${trigger.workflow_id}?run=${trigger.graph_run_id}`}
                        className="text-primary hover:text-primary/80"
                      >
                        View run
                      </Link>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!loading && !triggers.length && <p className="text-sm text-muted-foreground">No triggers recorded.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
